<template>
	<div class="db2-canvas">
		<div class="db2-legacy" v-if="!isV2">
			<v-icon size="48">mdi-view-dashboard-outline</v-icon>
			<h3>This dashboard needs to be rebuilt in the editor</h3>
			<p>Older card layouts are not loaded. Open Edit to add sections and cards.</p>
		</div>
		<template v-else>
			<DashboardSection
				v-for="section in orderedSections"
				:key="section.uniqueId"
				:section="section"
				:cards="cardsFor(section.uniqueId)"
				:is-edit-mode="isEditMode"
				:show-grid="showGrid"
				:selected="selectedSectionId === section.uniqueId"
				:dragging="sectionDrag && sectionDrag.id === section.uniqueId"
				:selected-id="selectedId"
				:time-period="timePeriod"
				:site-id="siteId"
				:project-id="projectId"
				:refresh-key="refreshKey"
				:cols="cols"
				:can-edit="canEdit"
				@select="$emit('select-section', section.uniqueId)"
				@drag-start="onSectionDragStart($event, section)"
				@toggle-collapsed="$emit('toggle-section-collapsed', { uniqueId: section.uniqueId, collapsed: $event })"
				@rename="$emit('rename-section', { uniqueId: section.uniqueId, name: $event })"
				@remove="$emit('remove-section', section.uniqueId)"
				@select-card="$emit('select-card', $event)"
				@duplicate-card="$emit('duplicate-card', $event)"
				@card-settings="$emit('card-settings', $event)"
				@remove-card="$emit('remove-card', $event)"
				@move-card="$emit('move-card', $event)"
				@append-log="$emit('append-log', $event)"
			/>
			<div class="db2-empty" v-if="!orderedSections.length">
				<v-icon size="48">mdi-view-dashboard-outline</v-icon>
				<h3>No sections yet</h3>
				<p v-if="isEditMode">Add a section, then add cards from the left palette.</p>
			</div>
			<button
				v-if="isEditMode"
				type="button"
				class="db2-add-section"
				@click="onAddSection"
			>
				<v-icon>mdi-plus</v-icon>
				<span>Add Section</span>
			</button>
		</template>
	</div>
</template>

<script>
import DashboardSection from './DashboardSection.vue';
import { isV2Dashboard, GRID_COLUMNS } from './dashboardModel';

/**
 * Dashboard canvas: sections and add-section control.
 */
export default {
	name: 'DashboardCanvas',
	components: { DashboardSection },
	props: {
		draft: { type: Object, required: true },
		isEditMode: { type: Boolean, default: false },
		showGrid: { type: Boolean, default: false },
		selectedId: { type: String, default: null },
		selectedSectionId: { type: String, default: null },
		timePeriod: { type: String, default: 'week' },
		siteId: { type: String, default: null },
		projectId: { type: String, default: null },
		refreshKey: { type: Number, default: 0 },
		cols: { type: Number, default: GRID_COLUMNS },
		canEdit: { type: Boolean, default: false }
	},
	data() {
		return { sectionDrag: null };
	},
	beforeDestroy() {
		this.clearSectionDrag();
	},
	computed: {
		/**
		 * v2 schema flag.
		 * @returns {boolean}
		 */
		isV2() {
			return isV2Dashboard(this.draft) || this.isEditMode;
		},
		/**
		 * Sections in sort order.
		 * @returns {Array}
		 */
		orderedSections() {
			return (this.draft.sections || []).slice().sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
		}
	},
	methods: {
		/**
		 * Add a section and drop Materialize :focus fill.
		 * @param {MouseEvent} e
		 */
		onAddSection(e) {
			if (e && e.currentTarget && e.currentTarget.blur) e.currentTarget.blur();
			this.$emit('add-section');
		},
		/**
		 * Start reordering sections from a header handle.
		 * @param {MouseEvent} e
		 * @param {object} section
		 */
		onSectionDragStart(e, section) {
			if (!this.isEditMode || !section) return;
			if (e && e.preventDefault) e.preventDefault();
			this.$emit('select-section', section.uniqueId);
			this.clearSectionDrag();
			this.sectionDrag = { id: section.uniqueId };
			window.addEventListener('mousemove', this.onSectionDragMove);
			window.addEventListener('mouseup', this.onSectionDragEnd);
		},
		/**
		 * Swap section order when the pointer crosses another section midpoint.
		 * @param {MouseEvent} e
		 */
		onSectionDragMove(e) {
			if (!this.sectionDrag) return;
			const ids = this.orderedSections.map(s => s.uniqueId);
			const from = ids.indexOf(this.sectionDrag.id);
			if (from < 0) return;
			const nodes = this.$el ? this.$el.querySelectorAll('.db2-section') : [];
			let insertAt = 0;
			for (let i = 0; i < nodes.length; i++) {
				const r = nodes[i].getBoundingClientRect();
				if (e.clientY > r.top + r.height / 2) insertAt = i + 1;
			}
			if (insertAt > from) insertAt -= 1;
			insertAt = Math.max(0, Math.min(ids.length - 1, insertAt));
			if (insertAt === from) return;
			const next = ids.slice();
			next.splice(from, 1);
			next.splice(insertAt, 0, this.sectionDrag.id);
			this.$emit('reorder-sections', next);
		},
		/**
		 * End a section reorder gesture.
		 */
		onSectionDragEnd() {
			this.$emit('reorder-sections-end');
			this.clearSectionDrag();
		},
		/**
		 * Drop section-reorder window listeners.
		 */
		clearSectionDrag() {
			window.removeEventListener('mousemove', this.onSectionDragMove);
			window.removeEventListener('mouseup', this.onSectionDragEnd);
			this.sectionDrag = null;
		},
		/**
		 * Start a move on the section that owns this card (palette drop).
		 * @param {object} card
		 * @param {MouseEvent} e
		 */
		beginCardDrag(card, e) {
			const sections = this.$children || [];
			let hit = null;
			for (let i = 0; i < sections.length; i++) {
				const child = sections[i];
				if (!child || child.$options.name !== 'DashboardSection') continue;
				if (!hit) hit = child;
				const list = child.cards || [];
				if (list.some(c => c.uniqueId === card.uniqueId)) {
					hit = child;
					break;
				}
			}
			if (hit && hit.onDragStart) hit.onDragStart(e, card);
		},
		/**
		 * Cards belonging to a section.
		 * @param {string} sectionId
		 * @returns {Array}
		 */
		cardsFor(sectionId) {
			return (this.draft.cards || []).filter(c => c.sectionId === sectionId);
		}
	}
};
</script>
